// MACHINERY V2 (Stroje) — gateway → MachineryV2Telemetry ADAPTER (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 + §6. Manager rulings B
// (status-preserving read) and B2 (Offline / TelemetryStale rows).
//
// ===================================================================
//  THE ONLY PLACE THE WIRE TOUCHES THE 9-STATE CONTRACT.
// ===================================================================
// Every fleet surface reads telemetry through here, so the day a richer
// per-machine source lands (the DEFERRED Connectivity Backend work in §6 —
// program line, alarms, rpm, part counts, freshness, SignalR) it is swapped in
// BEHIND this module and not one component changes.
//
// WHAT IS ON THE WIRE TODAY, AND WHAT IS NOT:
//   - GET /v1/gateway/machines returns one GatewayMachineSummaryDto per
//     gateway-known machine, carrying the DECODED status name ("Running" |
//     "Idle" | "Stopped" | "Alarm" | "Offline" | "TelemetryStale").
//   - It carries NOTHING else we render: no program, no alarm code/message, no
//     rpm, no part count, no since/elapsed, no heartbeat age. Those stay
//     null/empty here. INVENT NOTHING — a fabricated field is a lie a shop
//     floor acts on.
//
// WHY WE DO NOT USE useMachineConnectedStatuses: that hook is the LIVE grid's,
// and its last step collapses the decoded status to a boolean (Set<string> of
// connected ids). Rendering `awaiting` for a machine the server just told us is
// Running is exactly the false statement the 9-state contract exists to
// prevent. So v2 reads the SAME endpoint with a status-preserving sibling call.
// The live hook and getConnectedIds() are deliberately left byte-untouched —
// the live grid keeps its exact behaviour — and this module is the ONLY
// consumer of the read below.
//
// ONE REQUEST PER TICK. useFleetTelemetry issues the parameterless list call
// (the live grid's batch endpoint) on mount and then every 60 s. Never per
// card, never a second endpoint.
//
// THE POLL IS INTERIM (review F8, ruling C(i)). Before it, the read was keyed
// only on the machine-id set: fetched once at mount and never again, under a
// header that says LIVE FLEET next to a pulsing dot. A machine going Running ->
// Alarm stayed frozen until a reload. 60 s makes that header truthful at
// minute-freshness. **SignalR push is the endgame and REPLACES this** — it is
// already logged as deferred Connectivity Backend work in ticket §6/§7. When it
// lands, delete the interval, not just the comment.
//
// FAIL CLOSED (SEC-REQ-9). Any error — network, 5xx, 403 cross-tenant, null
// body — yields an EMPTY map, so every machine falls to `unconnected` and the
// fleet looks like today's grid. Mirrors the .catch(() => new Set()) posture in
// machineLiveStatusApi. Never fail open.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useEffect, useMemo, useState } from "react";

import api from "../../../../core/api";
import {
  MachineryV2Conn,
  MachineryV2Status,
  MachineryV2Telemetry,
  unconnectedTelemetry,
} from "./cardStates";

/** Minimal shape of a GatewayMachineSummaryDto row. Restated here (rather than
 *  imported from machineLiveStatusApi, whose copy is not exported) so the v2
 *  folder stays "one folder delete away" and the live file stays untouched. */
export interface FleetGatewayRow {
  machineId: string;
  /** Decoded enum name, or null when no status row exists yet. */
  status?: string | null;
}

export interface FleetWireMapping {
  conn: MachineryV2Conn;
  status: MachineryV2Status | null;
}

/**
 * >>> THE MAPPING TABLE — wire string -> (conn, status). <<<
 *
 * The single lookup that translates the gateway vocabulary into ours. A status
 * the gateway later starts sending is ONE row here, and nothing downstream
 * moves. Mirrors server MachineStatus.cs / MachineStatusCode: Running(1)
 * Idle(2) Stopped(3) Alarm(4) Offline(5) TelemetryStale(6).
 *
 * The two non-live rows (manager ruling B2):
 *  - "Offline" maps to the contract's OWN `offline` state, NOT to `awaiting`.
 *    A server verdict is never re-labelled — that is why deriveCardState's
 *    offline branch sits above its alarm branch. `conn` stays "live" because
 *    that is the truthful input (a summary row exists for this machine); the
 *    contradiction it would otherwise cause with the connectivity dot is
 *    resolved in PRESENTATION by deriveCardPresentation().dotConn, which forces
 *    the grey/X treatment for `offline`. Do NOT "fix" that by weakening this
 *    row — the input is not what is wrong.
 *  - "TelemetryStale" = agent heartbeating, feed dead -> conn "stale" with no
 *    status, which is the contract's own definition of `stale` (cardStates.ts).
 */
export const FLEET_WIRE_STATUS_MAP: Readonly<Record<string, FleetWireMapping>> = {
  Running: { conn: "live", status: "running" },
  Idle: { conn: "live", status: "idle" },
  Stopped: { conn: "live", status: "stopped" },
  Alarm: { conn: "live", status: "alarm" },
  Offline: { conn: "live", status: "offline" },
  TelemetryStale: { conn: "stale", status: null },
};

/** A row exists but its status is absent, or is a string we do not know. The
 *  machine IS reachable, so it is not `unconnected`; we simply cannot read it.
 *  status null resolves through deriveCardState to `awaiting`, whose honest
 *  meaning here is "connected but unreadable". */
const UNREADABLE: FleetWireMapping = { conn: "live", status: null };

/**
 * THE public surface: one pure function, wire row -> typed telemetry.
 *
 * undefined/null row = the gateway said nothing about this machine, i.e. it has
 * no MachineConnection at all -> `unconnected`, which renders NOTHING (no
 * badge, no dot, no strip). That is still ~99% of the fleet.
 *
 * Everything the wire does not carry stays null/empty — see the header.
 */
export const fleetTelemetryFor = (
  row?: FleetGatewayRow | null
): MachineryV2Telemetry => {
  if (!row) return unconnectedTelemetry();
  const key = typeof row.status === "string" ? row.status : "";
  const mapped = FLEET_WIRE_STATUS_MAP[key] || UNREADABLE;
  return { ...unconnectedTelemetry(), conn: mapped.conn, status: mapped.status };
};

/**
 * Readiness "connected" parity. The live grid feeds MidasCardRow from
 * `connectedIds.has(id)`, i.e. machineLiveStatusApi's `isLiveStatus`, which
 * whitelists Running/Idle/Stopped/Alarm ONLY — Offline(5) and
 * TelemetryStale(6) are NOT connected. Expressed here, in the module that owns
 * the wire semantics, so no component has to re-decide it. (This is a
 * READINESS gate, not a card-state decision: card state comes exclusively from
 * deriveCardPresentation.)
 */
export const isGatewayLive = (t: MachineryV2Telemetry): boolean =>
  t.status !== null && t.status !== "offline";

const getRequestConfig = () => {
  const baseURL = (window as any).app?.config?.machineGatewayApiBaseUrl;
  return baseURL ? { baseURL } : {};
};

/** The status-preserving sibling of getConnectedIds(): same endpoint, same
 *  single parameterless request, same fail-closed posture — it just keeps the
 *  decoded status instead of collapsing it to a boolean. */
const fetchFleetTelemetry = (): Promise<Map<string, MachineryV2Telemetry>> =>
  api
    .get<FleetGatewayRow[] | null>("/v1/gateway/machines", getRequestConfig())
    .then((res) => {
      const map = new Map<string, MachineryV2Telemetry>();
      const rows = res.data || [];
      for (const row of rows) {
        if (row && row.machineId) map.set(row.machineId, fleetTelemetryFor(row));
      }
      return map;
    })
    .catch(() => new Map<string, MachineryV2Telemetry>());

/** Interim poll cadence. Replaced wholesale by the deferred SignalR push. */
export const FLEET_TELEMETRY_POLL_MS = 60000;

/**
 * Batch telemetry for the fleet: fetch on mount, then every
 * FLEET_TELEMETRY_POLL_MS.
 *
 * `machineIds` is only a stable dep key (the request itself is parameterless
 * and returns the caller's whole customer-scoped set): an unchanged on-page
 * machine set does not restart the poll, and an EMPTY set — e.g. the
 * decommissioned scope, which renders no live surface — skips it entirely.
 *
 * PAUSED WHILE HIDDEN. A background tab must not keep hitting the gateway once
 * a minute forever; browsers also throttle timers there, which would produce a
 * ragged cadence rather than a stopped one. On `visibilitychange` back to
 * visible we refresh IMMEDIATELY (the data on screen is by definition stale)
 * and re-arm the interval, so the user never reads a minute-old fleet while
 * looking straight at it.
 *
 * The lastKeyRef guard the live hook once had is deliberately absent, for the
 * reason documented there: under React 18 StrictMode it suppressed the only
 * surviving fetch. The [key] dep already dedupes re-renders.
 */
export const useFleetTelemetry = (
  machineIds: string[]
): Map<string, MachineryV2Telemetry> => {
  const [telemetry, setTelemetry] = useState<Map<string, MachineryV2Telemetry>>(
    () => new Map()
  );

  const key = useMemo(
    () => Array.from(new Set(machineIds.filter(Boolean))).sort().join(","),
    [machineIds]
  );

  useEffect(() => {
    if (!key) {
      setTelemetry(new Map());
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const refresh = () => {
      fetchFleetTelemetry().then((map) => {
        if (!cancelled) setTelemetry(map);
      });
    };

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      timer = setInterval(refresh, FLEET_TELEMETRY_POLL_MS);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      refresh();
      start();
    };

    refresh();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [key]);

  return telemetry;
};

export default fleetTelemetryFor;
